import { FC } from "react";
import styled, { css } from "styled-components";

import Text from "@/components/Text";
import TestCompetitionBadge from "./TestCompetitionBadge";
import { OptimisationTestUnitResource } from "@/types/resources";
import { scrollBarStyles } from "@/utils/theme";

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  display: flex;
  padding: 4px;
  margin-bottom: 12px;
  overflow-x: auto;
  ${scrollBarStyles};
`;

const StyledText = styled(Text)``;

const Button = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  flex: 1;
  margin-right: 8px;
  background: transparent;
  padding: 0 16px;
  height: 24px;
  border-radius: 4px;
  transition: 0.2s ease-out border, 0.2s ease-out box-shadow;
  border: solid 1px transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:last-of-type {
    margin: 0;
  }
  ${StyledText} {
    transition: 0.2s ease-out color;
  }
  ${({ $active, theme }) =>
    $active &&
    css`
      box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.08);
      border: 1px solid ${theme.colors.purple};
    `}
  ${({ $active, theme }) =>
    !$active &&
    css`
      &:hover {
        ${StyledText} {
          color: ${theme.colors.purple};
        }
      }
    `}
    &:disabled {
    cursor: not-allowed;
    ${StyledText} {
      color: ${({ theme }) => theme.colors.lightGray};
    }
  }
`;

interface Props {
  selectedBatchs: number[];
  setSelectedBatches: React.Dispatch<React.SetStateAction<number[]>>;
  availableBatches: number[] | undefined;
  tests?: OptimisationTestUnitResource[];
}

const TestsBatchFilter: FC<Props> = ({
  selectedBatchs,
  setSelectedBatches,
  availableBatches,
  tests,
}) => {
  const onButtonClick = (i: number) => {
    if (i === 0) {
      return setSelectedBatches((prev) => (prev.includes(i) ? [] : [0]));
    }

    return setSelectedBatches((prev) => {
      if (!prev.includes(0)) {
        return prev.includes(i)
          ? prev.filter((batchNumber) => batchNumber !== i)
          : [...prev, i];
      }

      return [i];
    });
  };

  const checkIfHasCBatch = (cBatch: number, batch: number) => {
    return !!tests?.find(
      (test) => test.cBatch === cBatch && test.batch === batch
    );
  };

  return (
    <Box>
      <Button
        $active={!selectedBatchs.length}
        onClick={() => setSelectedBatches([])}
      >
        <Text
          as="span"
          $type="smallBold"
          $color={!selectedBatchs.length ? "purple" : "gray"}
        >
          All
        </Text>
      </Button>
      {Array.from({ length: 11 }, (_, i) => (
        <Button
          key={i}
          $active={selectedBatchs.includes(i)}
          disabled={!availableBatches?.includes(i)}
          onClick={() => onButtonClick(i)}
        >
          <StyledText
            as="span"
            $type="smallBold"
            $color={selectedBatchs.includes(i) ? "purple" : "gray"}
          >
            T{i}
          </StyledText>
          {checkIfHasCBatch(1, i) && (
            <TestCompetitionBadge
              isSmall
              inactive={!availableBatches?.includes(i)}
              type={1}
            />
          )}
          {checkIfHasCBatch(2, i) && (
            <TestCompetitionBadge
              isSmall
              inactive={!availableBatches?.includes(i)}
              type={2}
            />
          )}
        </Button>
      ))}
    </Box>
  );
};

export default TestsBatchFilter;
